import { z } from 'zod';

export const TierSchema = z.enum(['free', 'pluss']);
export const BinColorSchema = z.enum(['green', 'blue', 'red']);
export const Vec2Schema = z.tuple([z.number(), z.number()]);

export const SceneSchema = z.object({
  environment: z.string(),
  timeOfDay: z.enum(['day', 'evening', 'night']),
  weather: z.enum(['clear', 'rain', 'snow', 'wind']),
  season: z.enum(['spring', 'summer', 'autumn', 'winter']),
});

export const VoiceSchema = z.object({
  intro: z.string(),
  hint: z.string(),
  success: z.string(),
});

export const RewardSchema = z.object({
  sticker: z.string(),
  celebration: z.string(),
  unlock: z.string().optional(),
});

export const LevelBaseSchema = z.object({
  id: z.string(),
  index: z.number().int().positive(),
  chapter: z.number().int().positive(),
  tier: TierSchema,
  title: z.string(),
  newConcept: z.string().optional(),
  scene: SceneSchema,
  voice: VoiceSchema,
  reward: RewardSchema,
});

export const BinSchema = z.object({
  color: BinColorSchema,
  target: z.boolean(),
  state: z.enum(['normal', 'tipped', 'snowy']).optional(),
  surprise: z.string().optional(),
});

export const SoppelStopSchema = z.object({
  t: z.number().min(0).max(1),
  side: z.enum(['left', 'right']),
  bins: z.array(BinSchema),
  litter: z.number().int().nonnegative().optional(),
});

export const StartPointSchema = z.object({
  preset: z.string(),
  action: z.enum(['tapVehicle', 'auto']),
  voice: z.string(),
});

export const FinishPointSchema = z.object({
  preset: z.string(),
  action: z.enum(['tapToDump', 'none']),
  voice: z.string(),
});

export const SoppelLevelSchema = LevelBaseSchema.extend({
  start: StartPointSchema.optional(),
  finish: FinishPointSchema.optional(),
  mode: z.enum(['tap', 'sortColor', 'pickLitter', 'recycle', 'mixed']),
  route: z.object({
    preset: z.string(),
    speed: z.number().positive(),
  }),
  stops: z.array(SoppelStopSchema),
  recycling: z
    .object({
      containers: z.array(BinColorSchema),
      bagsPerColor: z.number().int().positive(),
    })
    .optional(),
});

export const SnoGoalSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('clearArea'), threshold: z.number().min(0).max(1) }),
  z.object({
    type: z.literal('clearPath'),
    path: z.string(),
    threshold: z.number().min(0).max(1),
  }),
]);

export const SnoLevelSchema = LevelBaseSchema.extend({
  area: z.object({
    preset: z.string(),
    size: Vec2Schema,
  }),
  plow: z.object({
    width: z.number().positive(),
    speed: z.number().positive(),
  }),
  goal: SnoGoalSchema,
  snowfall: z.number().nonnegative(),
  headlightsRadius: z.number().positive().optional(),
  icePatches: z
    .array(
      z.object({
        pos: Vec2Schema,
        radius: z.number().positive(),
      })
    )
    .optional(),
  hiddenItems: z.array(
    z.object({
      id: z.string(),
      pos: Vec2Schema,
      reaction: z.string().optional(),
    })
  ),
  onComplete: z.string(),
});

export const BrannTaskSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('extinguish'),
    fires: z.array(
      z.object({
        anchor: z.string(),
        size: z.union([z.literal(1), z.literal(2), z.literal(3)]),
      })
    ),
  }),
  z.object({
    type: z.literal('ladder'),
    target: z.string(),
    holdSec: z.number().positive(),
  }),
  z.object({
    type: z.literal('refill'),
    hydrant: z.string(),
  }),
  z.object({
    type: z.literal('clearTree'),
    taps: z.number().int().positive(),
  }),
  z.object({
    type: z.literal('wash'),
    dirtAmount: z.number().min(0).max(1),
  }),
  z.object({
    type: z.literal('sprayFun'),
    targets: z.number().int().positive(),
  }),
]);

export const BrannLevelSchema = LevelBaseSchema.extend({
  drive: z.object({
    preset: z.string(),
    durationSec: z.number().positive(),
  }),
  tasks: z.array(BrannTaskSchema).min(1),
  tuning: z
    .object({
      extinguishSpeed: z.number().positive().optional(),
    })
    .optional(),
  ending: z.string(),
});

export const ChapterSchema = z.object({
  chapter: z.number().int().positive(),
  title: z.string(),
  newConcept: z.string().optional(),
});

export const GamesManifestSchema = z.object({
  version: z.number().int().positive(),
  locale: z.string(),
  freeLevelsPerGame: z.number().int().positive(),
  ux: z.object({
    minTouchTargetPx: z.number().positive(),
    idleHintSec: z.number().positive(),
    failStates: z.literal(false),
    timers: z.literal(false),
    maxLevelDurationSec: z.number().positive(),
    unlockMode: z.literal('playToUnlock'),
  }),
  colors: z.object({
    green: z.string(),
    blue: z.string(),
    red: z.string(),
  }),
  sharedSystems: z.array(z.string()),
  games: z.array(
    z.object({
      id: z.string(),
      title: z.string(),
      vehicle: z.string(),
      priority: z.number().int().positive(),
      status: z.enum(['design-ready', 'planned']),
      coreMechanic: z.string(),
      systems: z.array(z.string()),
      levelFile: z.string().nullable(),
    })
  ),
});

export const SoppelLevelsFileSchema = z.object({
  gameId: z.literal('soppelbilen'),
  version: z.number().int().positive(),
  start: StartPointSchema,
  finish: FinishPointSchema,
  chapters: z.array(ChapterSchema),
  levels: z.array(SoppelLevelSchema).length(15),
});

export const SnoLevelsFileSchema = z.object({
  gameId: z.literal('snobroyteren'),
  version: z.number().int().positive(),
  chapters: z.array(ChapterSchema),
  levels: z.array(SnoLevelSchema).length(15),
});

export const BrannLevelsFileSchema = z.object({
  gameId: z.literal('brannbilen'),
  version: z.number().int().positive(),
  tuning: z.object({
    holdSecPerFireSize: z.number().positive(),
    sirenAlwaysAvailable: z.boolean(),
  }),
  chapters: z.array(ChapterSchema),
  levels: z.array(BrannLevelSchema).length(15),
});

export type Tier = z.infer<typeof TierSchema>;
export type BinColor = z.infer<typeof BinColorSchema>;
export type Vec2 = z.infer<typeof Vec2Schema>;
export type Scene = z.infer<typeof SceneSchema>;
export type Voice = z.infer<typeof VoiceSchema>;
export type Reward = z.infer<typeof RewardSchema>;
export type LevelBase = z.infer<typeof LevelBaseSchema>;
export type SoppelLevel = z.infer<typeof SoppelLevelSchema>;
export type SoppelStop = z.infer<typeof SoppelStopSchema>;
export type StartPoint = z.infer<typeof StartPointSchema>;
export type FinishPoint = z.infer<typeof FinishPointSchema>;
export type Bin = z.infer<typeof BinSchema>;
export type SoppelLevelsFile = z.infer<typeof SoppelLevelsFileSchema>;
export type SnoLevel = z.infer<typeof SnoLevelSchema>;
export type BrannLevel = z.infer<typeof BrannLevelSchema>;
export type BrannTask = z.infer<typeof BrannTaskSchema>;
export type GamesManifest = z.infer<typeof GamesManifestSchema>;
export type GameId = 'soppelbilen' | 'snobroyteren' | 'brannbilen';
export type AnyLevel = SoppelLevel | SnoLevel | BrannLevel;
